import { getRoles } from "./p2pRegistrationHelpers";

/**
 * @public
 * @function getCategoriesAttachedToClassification
 * @param {object} settings Settings object
 * @param {Array} [settings.categories] Available Categories
 * @param {string|number} [settings.classificationId] id of selected classification
 * @returns {Array} Returns array of Categories that can be selected with this classification
 */
export const getCategoriesAttachedToClassification = ({
    categories,
    classificationId,
}) =>
    categories.filter(
        (category) =>
            category.attachedClassifications.length === 0 ||
            category.attachedClassifications.includes(Number(classificationId)),
    );

/**
 * @public
 * @function getNewCategoryValueToSet
 * @param {Array} categories Available Categories
 * @param {object} selectedClassification selected classification data object
 * @returns {Array} Returns array of the Category id that should be reset
 */
export const getNewCategoryValueToSet = (
    categories,
    selectedClassification,
) => {
    const selectedClassificationId = selectedClassification.id;
    const availableCategoriesForSelectedClassification =
        getCategoriesAttachedToClassification({
            categories,
            classificationId: selectedClassificationId,
        });
    let newCategoryValue = [];

    if (availableCategoriesForSelectedClassification.length === 1) {
        newCategoryValue = [
            `${availableCategoriesForSelectedClassification[0].id}`,
        ];
    }
    return newCategoryValue;
};

/**
 * @public
 * @function getCategoryFilter
 * @param {object} settings Settings object
 * @param {boolean} [settings.allowSubRegistrantToChangeRegRole] Event level setting for whether or not
 * subregistrant can change registration roles (team/individual)
 * @param {object} [settings.category] Category that will be passed from filter function
 * @param {boolean} [settings.enableIndividualRegistration] Event level setting to allow individual registrants
 * @param {boolean} [settings.enableTeams] Event level setting to allow team registrants
 * @param {boolean} [settings.hasParentRegistration] Flag for when we are coming to registration from Fundraising Center
 * @param {boolean} [settings.isChildParticipant] Flag that checks whether registrant is not Primary
 * @param {object} [settings.parentRegistration] Object of Primary registrant data coming from Fundraising Center
 * @param {Array} [settings.registrations] Array of all registrations currently saved
 * in Redux
 * @returns {boolean} When this function is passed through the categories.filter() the boolean return determines whether this
 * category can be included in the list based on settings
 */
export const getCategoryFilter = ({
    allowSubRegistrantToChangeRegRole,
    category,
    enableIndividualRegistration,
    enableTeams,
    hasParentRegistration,
    isChildParticipant,
    parentRegistration,
    registrations,
}) => {
    const { attachPrimary, attachSubregistrants } = category;

    if (isChildParticipant) {
        if (allowSubRegistrantToChangeRegRole) {
            return attachSubregistrants;
        }
        const roles = getRoles({
            category,
            enableIndividualRegistration,
            enableTeams,
        });
        const primaryRegRole = hasParentRegistration
            ? parentRegistration.Registration_Role
            : registrations[0].Registration_Role[0];
        const categoryHasPrimaryRegRole = roles
            .map((role) => role.value)
            .includes(primaryRegRole);
        return attachSubregistrants && categoryHasPrimaryRegRole;
    }
    return attachPrimary;
};

/**
 * @public
 * @function getCategoryFilteringFlags
 * @param {object} settings Settings object
 * @param {Array} [settings.Classification] Selected Classification stored in Redux
 * @param {number} [settings.currentRegistration] Index of registration we are currently viewing
 * @param {boolean} [settings.enableClassifications] Event level setting to allow classification to be used
 * @param {boolean} [settings.enableTeams] Event level setting to allow team registrants
 * @param {boolean} [settings.hasParentRegistration] Flag for when we are coming to registration from Fundraising Center
 * @param {object} [settings.values] Formik values for current page viewed
 * @returns {object} Return object of booleans that are used when filtering out the categories
 */
export const getCategoryFilteringFlags = ({
    Classification,
    currentRegistration,
    enableClassifications,
    enableTeams,
    hasParentRegistration,
    values,
}) => {
    const isAdditionalParticipant = currentRegistration > 0;
    const isChildParticipant =
        isAdditionalParticipant || !!hasParentRegistration;

    const hasClassifications =
        enableClassifications && Classification && Classification.length > 0;

    const hasPreSelectedTeam = enableTeams && !!values.Team;

    const hasPreSelectedTeamMember = hasPreSelectedTeam && !values.Team_Captain;

    const hasPreSelectedTeamCaptain = hasPreSelectedTeam && values.Team_Captain;

    return {
        isChildParticipant,
        hasClassifications,
        hasPreSelectedTeamCaptain,
        hasPreSelectedTeamMember,
    };
};

/**
 * @public
 * @function filterCategoriesBasedOnPresetsOrUserSelections
 * @param {object} settings Settings object
 * @param {boolean} [settings.allowSubRegistrantToChangeRegRole] Event level setting for whether or not
 * subregistrant can change registration roles (team/individual)
 * @param {Array} [settings.categories] Array of all available Categories before any are filtered out
 * @param {Array} [settings.Classification] Selected Classification stored in Redux
 * @param {Array} [settings.classifications] Array of Available Classification
 * @param {boolean} [settings.enableIndividualRegistration] Event level setting to allow individual registrants
 * @param {boolean} [settings.enableTeams] Event level setting to allow team registrants
 * @param {boolean} [settings.hasClassifications] Flag for if Classifications have been enabled at the event level and
 * if there classifications available
 * @param {boolean} [settings.hasPreSelectedTeamCaptain] Flag for if Team has been created
 * @param {boolean} [settings.hasPreSelectedTeamMember] Flag for if Team has been joined
 * @param {boolean} [settings.hasParentRegistration] Flag for when we are coming to registration from Fundraising Center
 * @param {boolean} [settings.isChildParticipant] Flag that checks whether registrant is not Primary
 * @param {object} [settings.parentRegistration] Object of Primary registrant data coming from Fundraising Center
 * @param {Array} [settings.registrations] Array of all registrations currently saved
 * in Redux
 * @returns {Array} Array of classifications that are available when all settings and user
 * selections are taken into account
 */
export const filterCategoriesBasedOnPresetsOrUserSelections = ({
    allowSubRegistrantToChangeRegRole,
    categories,
    Classification,
    classifications,
    enableIndividualRegistration,
    enableTeams,
    hasClassifications,
    hasParentRegistration,
    hasPreSelectedTeamCaptain,
    hasPreSelectedTeamMember,
    isChildParticipant,
    parentRegistration,
    registrations,
}) => {
    const classificationSettings =
        hasClassifications && classifications
            ? classifications.find(
                  (item) => item.id === Number(Classification[0]),
              )
            : {};

    const classificationTeamCaptainHasReachedMax =
        classificationSettings?.teamLimit > 0 &&
        classificationSettings?.teamSpotsLeft === 0;
    // There are several user selections that can change the categories that
    // are available for based their other selection.  Primary vs additional
    // is handled in getCategoryFilter
    let filteredCategories = categories
        ? categories.filter((category) =>
              getCategoryFilter({
                  category,
                  isChildParticipant,
                  allowSubRegistrantToChangeRegRole,
                  hasParentRegistration,
                  enableIndividualRegistration,
                  enableTeams,
                  parentRegistration,
                  registrations,
              }),
          )
        : [];

    //  Filter by Classification and Selected Team status.

    // Filter out Categories that are not attached to the selected Classification
    if (hasClassifications) {
        filteredCategories = getCategoriesAttachedToClassification({
            categories: filteredCategories,
            classificationId: Number(Classification),
        });
    }

    // Filter out Categories that only allow you to create a team when
    // max number of teams that can be created has been reached
    if (hasClassifications && classificationTeamCaptainHasReachedMax) {
        filteredCategories = filteredCategories.filter(
            (filteredCategory) =>
                filteredCategory.attachTeamMembers ||
                filteredCategory.attachIndividual,
        );
    }

    if (hasPreSelectedTeamMember) {
        filteredCategories = filteredCategories.filter(
            (filteredCategory) => filteredCategory.attachTeamMembers,
        );
    }

    if (hasPreSelectedTeamCaptain) {
        filteredCategories = filteredCategories.filter(
            (filteredCategory) => filteredCategory.attachTeamCaptains,
        );
    }

    return filteredCategories;
};

/**
 * @public
 * @function getHasCategoriesWithSubregistrantEnabled
 * @param {object} settings Settings object
 * @param {Array} [settings.categories] Array of all available Categories before any are filtered out
 * @param {boolean} [settings.hasSelectedClassification] Has the registrant selected a Classification
 * @param {Array} [settings.selectedClassification] Selected Classification object
 * @returns {boolean} Return boolean of whether there are categories available for a subregistrant
 */
export const getHasCategoriesWithSubregistrantEnabled = ({
    categories,
    hasSelectedClassification,
    selectedClassification,
}) => {
    let hasCategoriesWithSubregistrantsEnabled = false;
    if (categories.length > 0) {
        const categoriesFilteredByClassificationIfSelected = categories?.filter(
            (category) => {
                if (hasSelectedClassification) {
                    return (
                        category?.attachedClassifications?.length === 0 ||
                        category?.attachedClassifications.includes(
                            selectedClassification.id,
                        )
                    );
                }
                return category;
            },
        );

        if (categoriesFilteredByClassificationIfSelected.length > 0) {
            hasCategoriesWithSubregistrantsEnabled =
                categoriesFilteredByClassificationIfSelected?.some(
                    (category) =>
                        category.attachSubregistrants === true &&
                        // attachTeamCaptain is not allowed for subregistrant so we need to disable
                        // button if those are the only Categories available
                        (category.attachIndividual ||
                            category.attachTeamMembers),
                );
        }
    }

    return hasCategoriesWithSubregistrantsEnabled;
};
